import { createTheme } from '@mui/material/styles'

// ¿Documento de la app demo (/demo/*)? Se decide al cargar (la demo vive en su
// propio documento/iframe). Controla el zoom de densidad y la compensación de 100vh.
const IS_DEMO_DOC =
  typeof window !== 'undefined' && /^\/demo(\/|$)/.test(window.location.pathname)
// La pantalla/kiosko de TV (/pantalla/:slug) ocupa el 100% sin el zoom 0.9 global
// (que dejaría una franja negra a la derecha/abajo en un mural 16:9 full-screen).
const IS_KIOSK_DOC =
  typeof window !== 'undefined' && /^\/[^/]+\/pantalla\/?$/.test(window.location.pathname)
const NO_ZOOM = IS_DEMO_DOC || IS_KIOSK_DOC

// Tokens del design system "Sala de Mando" (fuente: design_handoff_cuartel_digital/app/styles.css)
const tokens = {
  dark: {
    bg: '#090c13',
    bg1: '#0e131e',
    bg2: '#141b29',
    bg3: '#1b2433',
    bgHover: '#202b3d',
    line: 'rgba(255,255,255,0.07)',
    line2: 'rgba(255,255,255,0.13)',
    ink: '#eef1f7',
    inkMute: '#a2acbf',
    inkDim: '#6f7a90',
    red: '#ff3b40',
    redDeep: '#c11a24',
    redHover: '#ff5054',
    redGlow: 'rgba(255,59,64,0.30)',
    gold: '#f2b32c',
    goldHover: '#f7c04a',
    goldText: '#1c1306',
    hiVis: '#ecd40a',
    cyan: '#2bb8d9',
    green: '#29c46b',
    amber: '#f5a524',
    topbar: 'rgba(9,12,19,0.82)',
    selection: 'rgba(255,59,64,0.30)',
    scrollThumb: '#1b2433',
    scrollThumbHover: '#202b3d',
  },
  light: {
    bg: '#eaeef4',
    bg1: '#ffffff',
    bg2: '#f3f6fb',
    bg3: '#e6ebf3',
    bgHover: '#dde6f0',
    line: 'rgba(14,21,36,0.10)',
    line2: 'rgba(14,21,36,0.17)',
    ink: '#0f1524',
    inkMute: '#4d586c',
    inkDim: '#6d7589',
    red: '#d61f2a',
    redDeep: '#a5141d',
    redHover: '#c11a24',
    redGlow: 'rgba(214,31,42,0.22)',
    gold: '#b3790a',
    goldHover: '#8a5d08',
    goldText: '#1c1306',
    hiVis: '#e0c00a',
    cyan: '#0c87a6',
    green: '#15924f',
    amber: '#bd7e0a',
    topbar: 'rgba(245,247,251,0.82)',
    selection: 'rgba(214,31,42,0.22)',
    scrollThumb: '#c4ccd8',
    scrollThumbHover: '#aeb8c8',
  },
}

const FONT_UI = '"Manrope", system-ui, -apple-system, sans-serif'
const FONT_DISPLAY = '"Bebas Neue", sans-serif'
const FONT_MONO = '"JetBrains Mono", monospace'

// Configuración base compartida entre ambos temas
const baseConfig = {
  typography: {
    fontFamily: FONT_UI,
    // Claves custom del contrato de tema (MUI las pasa tal cual)
    fontFamilyDisplay: FONT_DISPLAY,
    fontFamilyMono: FONT_MONO,
    h1: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 400,
      fontSize: '2.75rem', // 44px — título de página
      lineHeight: 0.86,
      letterSpacing: '0.01em',
    },
    h2: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 400,
      fontSize: '2.25rem', // 36px
      lineHeight: 0.9,
      letterSpacing: '0.01em',
    },
    h3: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 400,
      fontSize: '1.75rem', // 28px
      lineHeight: 0.9,
      letterSpacing: '0.01em',
    },
    h4: {
      fontSize: '1.0625rem', // 17px — título de topbar
      fontWeight: 800,
      letterSpacing: '-0.01em',
      lineHeight: 1.1,
    },
    h5: {
      fontSize: '0.9375rem',
      fontWeight: 800,
      letterSpacing: '-0.005em',
    },
    h6: {
      fontSize: '0.875rem', // 14px — título de panel
      fontWeight: 800,
      letterSpacing: '-0.005em',
    },
    body1: {
      fontSize: '0.875rem', // 14px
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.8125rem', // 13px — body/tabla
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 700,
      fontSize: '0.8125rem',
    },
    caption: {
      fontSize: '0.75rem',
    },
  },
  shape: {
    borderRadius: 9,
  },
}

// Componentes base compartidos
const getComponents = (mode) => {
  const t = tokens[mode]
  return {
    MuiCssBaseline: {
      styleOverrides: {
        // Densidad global: 90% de zoom como nuevo "100%" del dashboard (antes era
        // 80% — el usuario lo quería un poco más grande). Va en el <body> para que
        // los portales (menús, diálogos, autocompletes, tooltips) queden a la misma
        // escala que la UI que los dispara. Excepción: el iframe /demo (documento
        // propio) se queda a 1.0 — ahí el zoom reducido se veía demasiado pequeño.
        body: {
          zoom: NO_ZOOM ? 1 : 0.9,
          // Compensación de alto: bajo zoom 0.9 un 100vh rendea al 90% del viewport
          // y deja una franja abajo → se divide por 0.9. En /demo (zoom 1) queda en
          // 100vh. La consumen los shells con min-height de pantalla (var global,
          // demo-aware, una sola fuente de verdad).
          '--cd-full-vh': NO_ZOOM ? '100vh' : 'calc(100vh / 0.9)',
        },
        '@keyframes cd-pulse': {
          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
          '50%': { opacity: 0.4, transform: 'scale(0.75)' },
        },
        '*::-webkit-scrollbar': {
          width: 10,
          height: 10,
        },
        '*::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: t.scrollThumb,
          borderRadius: 8,
          border: `2px solid ${t.bg}`,
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: t.scrollThumbHover,
        },
        '::selection': {
          backgroundColor: t.selection,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          fontSize: 13,
          borderRadius: 9,
          padding: '9px 16px',
          transition: 'all .14s',
        },
        containedPrimary: {
          backgroundColor: t.red,
          boxShadow: `0 4px 14px ${t.redGlow}`,
          '&:hover': {
            backgroundColor: t.redHover,
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 14px ${t.redGlow}`,
          },
        },
        containedSecondary: {
          backgroundColor: t.gold,
          color: t.goldText,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: t.goldHover,
            transform: 'translateY(-1px)',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 13,
          border: `1px solid ${t.line}`,
          backgroundImage: 'none',
          boxShadow: mode === 'light' ? '0 1px 2px rgba(14,21,36,0.04)' : 'none',
          transition: 'border-color .15s, transform .15s, box-shadow .15s',
          '&:hover': {
            transform: 'translateY(-2px)',
            borderColor: t.line2,
            boxShadow: mode === 'light' ? '0 8px 24px rgba(14,21,36,0.08)' : 'none',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 13,
        },
        outlined: {
          border: `1px solid ${t.line}`,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 9,
            '&.Mui-focused fieldset': {
              borderColor: t.red,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: t.topbar,
          backdropFilter: 'blur(16px) saturate(160%)',
          backgroundImage: 'none',
          color: t.ink,
          boxShadow: 'none',
          borderBottom: `1px solid ${t.line}`,
          borderRadius: 0,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: t.bg1,
          backgroundImage: 'none',
          borderRadius: 0,
          borderRight: `1px solid ${t.line}`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${t.line}`,
        },
        head: {
          fontFamily: FONT_MONO,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: t.inkDim,
        },
      },
    },
  }
}

// Tema claro — "Snow Operations"
export const lightTheme = createTheme({
  ...baseConfig,
  palette: {
    mode: 'light',
    primary: {
      main: '#d61f2a',
      light: '#ff3b40',
      dark: '#a5141d',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#b3790a',
      light: '#f2b32c',
      dark: '#8a5d08',
      contrastText: '#1c1306',
    },
    background: {
      default: '#eaeef4',
      paper: '#ffffff',
    },
    text: {
      primary: '#0f1524',
      secondary: '#4d586c',
      disabled: '#6d7589',
    },
    divider: 'rgba(14,21,36,0.10)',
    error: {
      main: '#d61f2a',
    },
    warning: {
      main: '#bd7e0a',
    },
    success: {
      main: '#15924f',
    },
    info: {
      main: '#0c87a6',
    },
    custom: {
      bg2: '#f3f6fb',
      bg3: '#e6ebf3',
      bgHover: '#dde6f0',
      inkDim: '#6d7589',
      line2: 'rgba(14,21,36,0.17)',
      hiVis: '#e0c00a',
      gold: '#b3790a',
    },
  },
  components: getComponents('light'),
})

// Tema oscuro (principal de la app)
export const darkTheme = createTheme({
  ...baseConfig,
  palette: {
    mode: 'dark',
    primary: {
      main: '#ff3b40',
      light: '#ff5559',
      dark: '#c11a24',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f2b32c',
      light: '#f7c64f',
      dark: '#b3790a',
      contrastText: '#1c1306',
    },
    background: {
      default: '#090c13',
      paper: '#0e131e',
    },
    text: {
      primary: '#eef1f7',
      secondary: '#a2acbf',
      disabled: '#6f7a90',
    },
    divider: 'rgba(255,255,255,0.07)',
    error: {
      main: '#ff3b40',
    },
    warning: {
      main: '#f5a524',
    },
    success: {
      main: '#29c46b',
    },
    info: {
      main: '#2bb8d9',
    },
    custom: {
      bg2: '#141b29',
      bg3: '#1b2433',
      bgHover: '#202b3d',
      inkDim: '#6f7a90',
      line2: 'rgba(255,255,255,0.13)',
      hiVis: '#ecd40a',
      gold: '#f2b32c',
    },
  },
  components: getComponents('dark'),
})

// Exportar tema por defecto (claro) para compatibilidad
const theme = lightTheme
export default theme
